import type { EntryKey } from "@pinia/colada";
import type { MaybeRefOrGetter } from "vue";
import type { ServerTableResponse } from "@/components/ui/data-table/composables/server-table";
import type { InsertWebhook, Webhook, WebhookEventLog } from "@/schemas/webhook.schema";
import type { Filter } from "@/types/backend-filter";
import { useMutation, useQuery, useQueryCache } from "@pinia/colada";
import { toValue } from "vue";
import { webhookApi } from "@/api/webhook.api";
import { SUBSCRIPTION_LIMITS_QUERY_KEYS } from "@/queries/subscription-limits.query";
import { api } from "@/services/api";
import { useProjectStore } from "@/stores/project.store";
import { hasPermission } from "@/stores/user-profile.store";

// ==================== QUERY KEYS ====================

export const WEBHOOKS_QUERY_KEYS = {
  root: ["webhooks"] as const,
  list: (projectId?: string | null) =>
    [...WEBHOOKS_QUERY_KEYS.root, "list", { projectId }] as const,
  eventLogs: (webhookId: string) => [...WEBHOOKS_QUERY_KEYS.root, webhookId, "event-logs"] as const,
  eventLogsWithParams: (webhookId: string, params: any) =>
    [...WEBHOOKS_QUERY_KEYS.eventLogs(webhookId), params] as const,
  eventLogsCount: (webhookId: string, where?: any) =>
    [...WEBHOOKS_QUERY_KEYS.eventLogs(webhookId), "count", where] as const,
};

// ==================== WEBHOOKS CRUD ====================

/**
 * Query hook for fetching webhooks
 *
 * Features:
 * - **Project filtering**: Automatically filters by selected project via X-Project-ID header
 * - Smart caching with 5min stale time
 * - Query refetches when selected project changes
 * - Only enabled for users with webhook read permission
 */
export function useWebhooksQuery() {
  const projectStore = useProjectStore();

  return useQuery({
    // Include project ID in key so query refetches when project changes
    key: () => WEBHOOKS_QUERY_KEYS.list(projectStore.currentProjectId) as unknown as EntryKey,
    query: async () => {
      // Pass project header to filter by selected project
      const { data } = await api.get<Webhook[]>("/webhooks", {
        headers: projectStore.getProjectHeader(),
      });
      return data;
    },
    staleTime: 5 * 60_000,
    gcTime: 10 * 60_000,
    enabled: () => hasPermission("webhooks:read"),
  });
}

/**
 * Create new webhook
 *
 * Uses project header to create webhook in the selected project.
 */
export function useCreateWebhook() {
  const queryCache = useQueryCache();
  const projectStore = useProjectStore();

  return useMutation({
    mutation: async (payload: InsertWebhook) => {
      return await webhookApi.create(payload, {
        headers: projectStore.getProjectHeader(),
      });
    },
    onSuccess: () => {
      queryCache.invalidateQueries({ key: WEBHOOKS_QUERY_KEYS.root as any });
      queryCache.invalidateQueries({ key: SUBSCRIPTION_LIMITS_QUERY_KEYS.root as any });
    },
  });
}

/**
 * Update existing webhook
 *
 * Uses project header for access control.
 */
export function useUpdateWebhook() {
  const queryCache = useQueryCache();
  const projectStore = useProjectStore();

  return useMutation({
    mutation: async (payload: { id: string } & Partial<InsertWebhook>) => {
      const { id, ...body } = payload;
      return await webhookApi.updateById(id, body, {
        headers: projectStore.getProjectHeader(),
      });
    },
    onSuccess: () => {
      queryCache.invalidateQueries({ key: WEBHOOKS_QUERY_KEYS.root as any });
    },
  });
}

/**
 * Delete webhook
 *
 * Uses project header for access control.
 */
export function useDeleteWebhook() {
  const queryCache = useQueryCache();
  const projectStore = useProjectStore();

  return useMutation({
    mutation: async (id: string) => {
      return await webhookApi.deleteById(id, {
        headers: projectStore.getProjectHeader(),
      });
    },
    onSuccess: () => {
      queryCache.invalidateQueries({ key: WEBHOOKS_QUERY_KEYS.root as any });
      queryCache.invalidateQueries({ key: SUBSCRIPTION_LIMITS_QUERY_KEYS.root as any });
    },
  });
}

// ==================== WEBHOOK EVENT LOGS ====================

/**
 * Query for webhook event logs with server-side pagination
 * Compatible with useQueryTable composable
 *
 * Auto-refreshes every 5 seconds using PiniaColadaAutoRefetch plugin
 */
export function useWebhookEventLogsTableQuery(
  webhookId: MaybeRefOrGetter<string>,
  filter: MaybeRefOrGetter<Filter>
) {
  return useQuery({
    key: () =>
      WEBHOOKS_QUERY_KEYS.eventLogsWithParams(
        toValue(webhookId),
        toValue(filter)
      ) as unknown as EntryKey,
    query: async (): Promise<ServerTableResponse<WebhookEventLog>> => {
      const filterValue = toValue(filter);
      const id = toValue(webhookId);

      // Fetch data and count in parallel
      const [dataRes, countRes] = await Promise.all([
        api.get<WebhookEventLog[]>(`/webhooks/${id}/event-logs`, {
          params: { filter: filterValue },
        }),
        api.get<{ count: number }>(`/webhooks/${id}/event-logs/count`, {
          params: filterValue.where ? { where: filterValue.where } : undefined,
        }),
      ]);

      const { data } = dataRes;
      const { data: countData } = countRes;

      return {
        data,
        count: countData.count,
      };
    },
    staleTime: 0, // Always refetch when triggered
    gcTime: 5 * 60_000,
    autoRefetch: 5000, // Auto-refresh every 5 seconds (PiniaColadaAutoRefetch plugin)
    enabled: () => hasPermission("webhooks:read"),
  });
}

/**
 * Query for a single webhook event log by id
 *
 * Used to resolve a deep-linked event that is not present in the current
 * page of the event logs table (e.g. an older event reached via a shared
 * URL). The backend scopes the lookup to `webhookId` and the caller's
 * organization, so no extra authorization check is needed here.
 */
export function useWebhookEventLogQuery(
  webhookId: MaybeRefOrGetter<string>,
  eventId: MaybeRefOrGetter<string>
) {
  return useQuery({
    key: () =>
      [
        ...WEBHOOKS_QUERY_KEYS.eventLogs(toValue(webhookId)),
        "single",
        toValue(eventId),
      ] as unknown as EntryKey,
    query: async (): Promise<WebhookEventLog | null> => {
      const id = toValue(webhookId);
      const { data } = await api.get<WebhookEventLog[]>(`/webhooks/${id}/event-logs`, {
        params: { filter: { where: { id: toValue(eventId) }, limit: 1 } },
      });
      return data[0] ?? null;
    },
    enabled: () => !!toValue(eventId) && hasPermission("webhooks:read"),
  });
}

export function useRetryWebhookEvent() {
  const queryCache = useQueryCache();
  return useMutation({
    mutation: async (payload: { webhookId: string; eventId: string }) => {
      return await webhookApi.retryEvent(payload.webhookId, payload.eventId);
    },
    onSuccess: (_data, variables) => {
      // Invalidate event logs for this webhook
      queryCache.invalidateQueries({
        key: WEBHOOKS_QUERY_KEYS.eventLogs(variables.webhookId) as any,
      });
    },
  });
}
